import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

import {
  Checkbox,
  CheckboxProps,
  DropdownProps,
  Icon,
  InputProps,
  FormField,
  Radio,
  Button,
  Dropdown,
} from "semantic-ui-react";

import { ComponentPropsGetterC, ComponentPropsGetterR } from "react-table-6";

import { Table } from "react-lib/frameworks/Table";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import EditorColumn from "react-lib/appcon/common/EditorColumn";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";
import ModInfo from "react-lib/apps/common/ModInfo";

import CardTrainingKPISettingUX from "./CardTrainingKPISettingUX";

import {
  ACTIONS,
  BTN_ACTS,
  MasterOptions,
  OnEvent,
  PickedState,
  RunSequence,
  SetProp,
  State,
} from "./sequence/TrainingKPISetting";

// Types
type CardTrainingKPISettingProps = Pick<
  PickedState,
  "buttonLoadCheck" | "searchedItemListWithKey"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  TrainingKPISettingSequence: State["TrainingKPISettingSequence"];
  masterOptions: MasterOptions;
  divisionOptions: Record<string, any>[];
  languageUX?: any;
};

const IMAGES = {
  cautionSign: "/static/images/time-attendance/caution-sign.png",
};

const CardTrainingKPISetting = (props: CardTrainingKPISettingProps) => {
  const [isAddEmployeeMode, setIsAddEmployeeMode] = useState<boolean>(false);
  const [searchMode, setSearchMode] = useState<"employee" | "division">("employee");
  const [selectedAddDivision, setSelectedAddDivision] = useState<any>(null);
  const [selectedAddEmployee, setSelectedAddEmployee] = useState<Record<string, any> | null>(null);
  const [divisionEmployeeList, setDivisionEmployeeList] = useState<any[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [openModInfo, setOpenModInfo] = useState<boolean>(false);
  const [modConfirm, setModConfirm] = useState<any>(null);
  const [modInfoMessage, setModInfoMessage] = useState<string>("");

  const [selectedEmployee, setSelectedEmployee] = useState<Record<string, any> | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<number | string | null>(null);

  const [employeeTraining, setEmployeeTraining] = useState<
    Partial<Record<"course" | "hour" | "score", string>>
  >({});

  useEffect(() => {
    props.runSequence({ sequence: "TrainingKPISetting", restart: true });
  }, []);

  useEffect(() => {
    if (checkedIds.length === 0) {
      setEmployeeTraining({});
    }
  }, [checkedIds]);

  useEffect(() => {
    const element = document.querySelector(".blueSelectedRow");

    if (!selectedRowId || !element) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setSelectedRowId(null);
          }, 1000);
        }
      }
    });

    observer.observe(element);

    element.scrollIntoView({ behavior: "smooth" });
  }, [selectedRowId]);

  const handleChangeValue = useCallback(
    (index: number) => (e: SyntheticEvent | null, data: InputProps) => {
      const { name, value } = data;
      const items: any[] = props.TrainingKPISettingSequence?.items ?? [];

      items[index][name] = value;

      props.setProp("TrainingKPISettingSequence.items", [...items]);
    },
    [props.TrainingKPISettingSequence?.items]
  );

  const handleChangeEmployeeTraining = useCallback(
    (e: SyntheticEvent | null, data: InputProps) => {
      setEmployeeTraining((prev) => ({ ...prev, [data.name]: data.value }));
    },
    [props.TrainingKPISettingSequence?.items]
  );

  const handleCheckCheckbox = useCallback(
    (e: SyntheticEvent, data: CheckboxProps) => {
      let ids = [...checkedIds];

      if (data.checked) {
        ids.push(Number(data.name));
      } else {
        ids = ids.filter((id) => id !== Number(data.name));
      }

      setCheckedIds(ids);
    },
    [checkedIds]
  );

  const handleCheckboxAll = useCallback(
    (e: SyntheticEvent, data: CheckboxProps) => {
      const items = props.TrainingKPISettingSequence?.items ?? [];

      if (data.checked) {
        setCheckedIds(items.map((item) => item.employee));
      } else {
        setCheckedIds([]);
      }
    },
    [props.TrainingKPISettingSequence?.items]
  );

  const headers = useMemo(() => {
    const items = props.TrainingKPISettingSequence?.items ?? [];
    const isCheckedAll = checkedIds.length > 0 && items.length === checkedIds.length;

    return [
      items.length > 0 ? (
        <Checkbox key="checkbox-all" checked={isCheckedAll} onChange={handleCheckboxAll} />
      ) : null,
      "Employee code",
      "Employee name",
      "Position",
      "Affiliation",
      "Minimum Course/ month",
      "Minimum Hour/ month",
      "Minimum Score/ month",
    ];
  }, [checkedIds, handleCheckboxAll]);

  const filteredItems = useMemo(() => {
    const items = props.TrainingKPISettingSequence?.items ?? [];
    const filter = props.TrainingKPISettingSequence?.filter ?? {};

    return items
      .map((item, index) => ({ ...item, index }))
      .filter((item) => {
        const isNameMatch = filter.employeeName ? item.employee_name === filter.employeeName : true;
        const isPositionMatch = filter.positionName
          ? item.employee_position === filter.positionName
          : true;

        return isNameMatch && isPositionMatch;
      });
  }, [props.TrainingKPISettingSequence?.filter, props.TrainingKPISettingSequence?.items]);

  const formattedItems = useMemo(
    () =>
      filteredItems.map((item, index) => ({
        ...item,
        affiliateName: item.employee_affiliation || "",
        checkbox: (
          <div style={{ display: "grid", placeContent: "center" }}>
            <Checkbox
              style={{ marginTop: "0.2rem" }}
              checked={checkedIds.includes(item.employee)}
              name={String(item.employee)}
              onChange={handleCheckCheckbox}
            />
          </div>
        ),
        employeeCode: (
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto" }}>
            <label>{item.employee_code}</label>
            {item.employee_status === "INACTIVE" && (
              <label style={{ color: "#db2828", fontSize: "0.8rem", fontWeight: "bold" }}>
                INACTIVE
              </label>
            )}
          </div>
        ),
        minimumCourse: (
          <EditorColumn
            align="left"
            inputMin={0}
            inputSize="small"
            inputStyle={{ padding: "0 1rem" }}
            inputType="number"
            value={item.minimum_course_per_month}
            editing
            inputNumber
            valueOnFocus
            onDataChanged={(value) => {
              handleChangeValue(index)(null, {
                name: "minimum_course_per_month",
                value,
              });
            }}
          />
        ),
        minimumHour: (
          <EditorColumn
            align="left"
            inputMin={0}
            inputSize="small"
            inputStyle={{ padding: "0 1rem" }}
            inputType="number"
            value={item.minimum_hour_per_month}
            editing
            inputNumber
            valueOnFocus
            onDataChanged={(value) => {
              handleChangeValue(index)(null, { name: "minimum_hour_per_month", value });
            }}
          />
        ),
        minimumScore: (
          <EditorColumn
            align="left"
            inputMin={0}
            inputSize="small"
            inputStyle={{ padding: "0 1rem" }}
            inputType="number"
            value={item.minimum_score_per_month}
            editing
            inputNumber
            valueOnFocus
            onDataChanged={(value) => {
              handleChangeValue(index)(null, { name: "minimum_score_per_month", value });
            }}
          />
        ),
        positionName: item.employee_position || "",
        remove: (
          <div
            style={{
              color: "#db2828",
              cursor: "pointer",
              display: "flex",
              fontSize: "0.85rem",
              fontWeight: "bold",
              justifyContent: "center",
            }}
            onClick={() => {
              setModConfirm(item);
            }}
          >
            <Icon color="red" name="user" link />-{" "}
            <label style={{ fontSize: "0.8rem" }}>REMOVE</label>
          </div>
        ),
      })),
    [
      filteredItems,
      handleChangeValue,
      handleCheckCheckbox,
      props.masterOptions.division,
      props.masterOptions.positionAll,
    ]
  );

  const handleSearch = () => {
    const foundPosition = (props.masterOptions.positionAll ?? []).find(
      (option) => Number(option.value) === selectedPosition
    );

    props.runSequence({
      sequence: "TrainingKPISetting",
      action: ACTIONS.SEARCH,
      employeeName: selectedEmployee?.full_name || "",
      positionName: foundPosition?.text || "",
    });
  };

  const handleSave = () => {
    props.runSequence({
      sequence: "TrainingKPISetting",
      action: ACTIONS.SAVE,
    });
  };

  const handleGetTdProps: ComponentPropsGetterC = (state: any, rowInfo, column) => ({
    style:
      column?.id === "checkbox"
        ? {}
        : {
            borderBottom: "solid 1px rgba(0,0,0,0.05)",
            borderRight: "1px solid rgba(0,0,0,0.02)",
          },
  });

  const handleGetTrProps: ComponentPropsGetterR = (state: any, rowInfo) => ({
    className:
      selectedRowId && rowInfo?.original?.employee === selectedRowId ? "blueSelectedRow" : "",
  });

  const handleGetTrGroupProps: ComponentPropsGetterR = () => ({
    style: { borderBottom: "none" },
  });

  const handleGetTheadProps: ComponentPropsGetterR = () => ({
    style: { backgroundColor: "transparent", boxShadow: "none" },
  });

  const handleGetTheadThProps: ComponentPropsGetterC = (state: any, rowInfo, column) => ({
    style:
      column?.id === "checkbox"
        ? { border: "none" }
        : {
            backgroundColor: "var(--primary-theme-color)",
            boxShadow: "0 2px 7px 0 rgba(0,0,0,0.15)",
            color: "white",
          },
  });

  const handleToggleIsAddEmployeeMode = () => {
    setIsAddEmployeeMode(!isAddEmployeeMode);
    setSelectedAddEmployee(null);
    setSelectedAddDivision(null);
    setSearchMode("employee");
  };

  const handleClickAddEmployee = () => {
    if (searchMode === "employee" && selectedAddEmployee) {
      props.runSequence({
        sequence: "TrainingKPISetting",
        action: ACTIONS.ADD_EMPLOYEE,
        data: {
          type: "employee",
          ...selectedAddEmployee,
        },
        onError: () => {
          setModInfoMessage("This employee code is already in the list.");
          setOpenModInfo(true);
        },
        onSuccess: () => {
          setSelectedAddEmployee(null);
        },
      });
    }

    if (searchMode === "division" && selectedAddDivision) {
      props.runSequence({
        sequence: "TrainingKPISetting",
        action: ACTIONS.ADD_EMPLOYEE,
        data: {
          type: "division",
          value: selectedAddDivision.value,
          text: selectedAddDivision.text,
        },
        onError: (reason?: string) => {
          if (reason === "no-employees") {
            setModInfoMessage(`There is no employee in ${selectedAddDivision.text}.`);
          } else if (reason === "all-added") {
            setModInfoMessage(
              `All employees in ${selectedAddDivision.text} are already in the list.`
            );
          } else {
            setModInfoMessage("Unable to add employees from this division.");
          }
          setOpenModInfo(true);
        },
        onSuccess: () => {
          setSelectedAddDivision(null);
        },
      });
    }

    setSelectedEmployee(null);
    setSelectedPosition(null);
  };

  const handleSaveToEmployee = () => {
    const items = props.TrainingKPISettingSequence?.items ?? [];

    const updatedItems = items.map((item) =>
      checkedIds.includes(item.employee)
        ? {
            ...item,
            minimum_course_per_month: employeeTraining.course || "",
            minimum_hour_per_month: employeeTraining.hour || "",
            minimum_score_per_month: employeeTraining.score || "",
          }
        : item
    );

    props.setProp("TrainingKPISettingSequence.items", [...updatedItems]);

    setCheckedIds([]);
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("TrainingKPISettingSequence.filter", {
      ...props.TrainingKPISettingSequence?.filter,
      [name]: data.value,
    });
  };

  const handleCloseModInfo = () => {
    setOpenModInfo(false);
    setSelectedRowId(selectedAddEmployee?.id);
    setSelectedAddEmployee(null);
  };

  const handleCloseModConfirm = () => {
    setModConfirm(null);
  };

  const handleRemove = () => {
    if (modConfirm) {
      props.runSequence({
        sequence: "TrainingKPISetting",
        action: ACTIONS.REMOVE,
        data: modConfirm,
        onSuccess: handleCloseModConfirm,
      });
    }
  };

  const handleChangePosition = (e: SyntheticEvent, data: DropdownProps) => {
    setSelectedPosition(data.value ? Number(data.value) : "");
  };

  return (
    <div style={{ marginTop: "-1.5rem" }}>
      <CardTrainingKPISettingUX
        languageUX={props.languageUX}
        checkedIds={checkedIds}
        employeeTraining={employeeTraining}
        filter={props.TrainingKPISettingSequence?.filter}
        isAddEmployeeMode={isAddEmployeeMode}
        selectedPosition={selectedPosition}
        selectedEmployee={selectedAddEmployee}
        selectedDivision={selectedAddDivision}
        disableButton={
          (searchMode === "employee" && !selectedAddEmployee) ||
          (searchMode === "division" && !selectedAddDivision)
        }
        divisionOptions={props.divisionOptions}
        positionOptions={props.masterOptions.positionAll}
        onChangeEmployeeTraining={handleChangeEmployeeTraining}
        onChangeFilter={handleChangeFilter}
        onChangePosition={handleChangePosition}
        onClickAddEmployee={handleToggleIsAddEmployeeMode}
        onCLickAddEmployee={handleClickAddEmployee}
        onSaveToEmployee={handleSaveToEmployee}
        addRadioButton={
          isAddEmployeeMode ? (
            <>
              <FormField
                inline
                style={{ display: "flex", alignItems: "center", marginBottom: "0.5em" }}
              >
                <Radio
                  label={"Select Employee"}
                  checked={searchMode === "employee"}
                  onChange={() => setSearchMode("employee")}
                />
                <div style={{ flex: "0 0 300px", marginLeft: "1em" }}>
                  <SearchBoxWithKey
                    onEvent={props.onEvent}
                    id="ETA"
                    placeholder="Search Employee"
                    selectedItem={selectedAddEmployee?.id || null}
                    type="UserEmployee"
                    inline
                    searchedItemListWithKey={props.searchedItemListWithKey}
                    setSelected={(value, key, data) => setSelectedAddEmployee(data)}
                    onAdditionalUrlParams={() => ({
                      affiliate: props.TrainingKPISettingSequence?.filter?.division,
                    })}
                    disabled={searchMode !== "employee"}
                  />
                </div>
              </FormField>

              <FormField inline style={{ display: "flex", alignItems: "center" }}>
                <Radio
                  label={"Select Department"}
                  checked={searchMode === "division"}
                  onChange={() => setSearchMode("division")}
                />
                <div style={{ flex: "0 0 300px", marginRight: "1em" }}>
                  <Dropdown
                    placeholder="Select Division"
                    fluid
                    search
                    selection
                    options={props.divisionOptions}
                    value={selectedAddDivision?.value || null}
                    onChange={(e: any, { value }: any) => {
                      const selected = props.divisionOptions.find((d) => d.value === value);
                      setSelectedAddDivision(selected || null);
                    }}
                    disabled={searchMode !== "division"}
                  />
                </div>
                <ButtonLoadCheck
                  setProp={props.setProp}
                  color="green"
                  paramKey={BTN_ACTS.ADD_EMPLOYEE}
                  size="small"
                  title={props.languageUX === "en" ? "ADD" : "เพิ่ม"}
                  buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.ADD_EMPLOYEE]}
                  onClick={handleClickAddEmployee}
                  disabled={
                    (searchMode === "employee" &&
                      (!selectedAddEmployee || !selectedAddEmployee.id)) ||
                    (searchMode === "division" &&
                      (!selectedAddDivision || !selectedAddDivision.value))
                  }
                />
              </FormField>
            </>
          ) : null
        }
        buttonSave={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="green"
            paramKey={BTN_ACTS.SAVE}
            size="small"
            title="SAVE"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
            onClick={handleSave}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        employeeSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="ETA"
            placeholder="Search Employee"
            selectedItem={selectedEmployee?.id || null}
            type="UserEmployee"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelected={(value, key, data) => {
              setSelectedEmployee(data);
            }}
            onAdditionalUrlParams={() => ({
              affiliate: props.TrainingKPISettingSequence?.filter?.division,
            })}
          />
        }
        table={
          <div style={{ maxHeight: "calc(100vh - 20.5rem)", overflowY: "auto" }}>
            <Table
              style={{ border: "none" }}
              data={formattedItems}
              headers={headers}
              keys="checkbox,employeeCode,employee_name,positionName,affiliateName,minimumCourse,minimumHour,minimumScore,remove"
              minRows={3}
              showPagination={false}
              sortable={false}
              widths="42.5,^80,^125,^125,^125,^100,^100,^100,100"
              getTdProps={handleGetTdProps}
              getTheadProps={handleGetTheadProps}
              getTheadThProps={handleGetTheadThProps}
              getTrGroupProps={handleGetTrGroupProps}
              getTrProps={handleGetTrProps}
            />
          </div>
        }
      />

      <ModInfo
        buttonColor="green"
        open={openModInfo}
        titleColor="red"
        titleName="Warning"
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      >
        <div
          style={{
            fontWeight: "bold",
            lineHeight: 2,
            margin: "0.5rem 0px 0.5rem",
          }}
        >
          <img
            style={{
              marginBottom: "-2px",
              marginRight: "0.5rem",
              width: "17px",
            }}
            alt="caution-sign.icon"
            src={IMAGES.cautionSign}
          />
          <span>{modInfoMessage}</span>
        </div>
      </ModInfo>

      <ModConfirmCNMI
        approveButtonLoading={props.buttonLoadCheck?.[BTN_ACTS.REMOVE]}
        approveButtonText="Confirm"
        denyButtonText="Cancel"
        openModal={!!modConfirm}
        titleColor="red"
        titleName="Warning"
        onApprove={handleRemove}
        onCloseWithDimmerClick={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
        content={
          <div
            style={{
              lineHeight: 2,
              margin: "-0.5rem 0 -1.5rem",
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
              <img
                style={{
                  marginBottom: "-2px",
                  marginRight: "0.5rem",
                  width: "17px",
                }}
                alt="caution-sign.icon"
                src={IMAGES.cautionSign}
              />
              <span>Confirm removal of employee from KPI setting</span>
            </div>
            <div>
              This will remove then only going forward. It will not affect any KPI scores for
              previous months.
            </div>
          </div>
        }
      />
    </div>
  );
};

CardTrainingKPISetting.displayName = "CardTrainingKPISetting";

export default React.memo(CardTrainingKPISetting);
